/**
 * What it might cost.
 *
 * The first question a homeowner asks and the last thing a maker wants to answer from a
 * drawing -- so this is an ESTIMATE, a band rather than a number, from the maker's own
 * price bands rather than anything we invented. Every cabinet already carries a band
 * (`A`, `B`, `C`) for exactly this; the maker's `pricing` block says what a band costs,
 * what a door profile and a gloss finish add, and what stone is per metre.
 *
 * A maker without a `pricing` block gets no estimate anywhere: the app shows nothing rather
 * than a guess with someone else's name on it. Pure; no rounding until the last line.
 */

/** `$18,400` -- whole dollars, en-AU grouping. */
export const money = (n) => '$' + Math.round(n).toLocaleString('en-AU')

export function estimate(plan, maker) {
  const p = maker.pricing
  if (!p) return null
  const pick = (list, id) => list.find((x) => x.id === id) ?? list[0]
  const style = pick(maker.doorStyles, plan.doorStyleId)
  const colour = pick(maker.colours, plan.colourId)
  const handle = pick(maker.handles, plan.handleId)
  const bench = pick(maker.benchtops, plan.benchtopId)
  const splash = pick(maker.splashbacks, plan.splashbackId)

  const profile = p.doorStyles?.[style.id] ?? 1
  const gloss = colour.gloss > 0.4 ? (p.glossFactor ?? 1) : 1

  let cabinets = 0, fronts = 0, fillers = 0, baseMetres = 0
  for (const run of plan.runs) {
    for (const item of run.items) {
      if (item.gap) continue
      if (item.filler) { fillers += 1; continue }
      const spec = maker.cabinets.find((c) => c.id === item.specId)
      if (!spec) continue
      const band = p.bands?.[spec.band] ?? p.bands?.A ?? 0
      // The door profile and the gloss are paid on what has doors; a fridge space has none.
      const dressed = (spec.doors + spec.drawers) > 0
      cabinets += band * (dressed ? profile * gloss : 1)
      fronts += spec.doors + spec.drawers
      if (run.kind === 'base') baseMetres += item.w / 1000
    }
  }
  const handles = fronts * (p.handles?.[handle.id] ?? 0)
  const benchtop = baseMetres * (p.benchtop?.[bench.id] ?? 0)
  const splashRate = splash.colour ? (p.splashback?.[splash.id] ?? 0) : (p.benchtop?.[bench.id] ?? 0) * 0.5
  const splashback = baseMetres * splashRate
  const fill = fillers * (p.filler ?? 0)
  const supply = cabinets + handles + benchtop + splashback + fill
  const install = supply * (p.installRate ?? 0)
  const total = supply + install
  const spread = p.spread ?? 0.08
  const to100 = (n) => Math.round(n / 100) * 100
  const low = to100(total * (1 - spread)), high = to100(total * (1 + spread))
  return {
    currency: p.currency ?? 'AUD',
    low, high, total: Math.round(total),
    text: `${money(low)}–${money(high)}`,
    lines: [
      { label: 'Cabinets', amount: Math.round(cabinets) },
      { label: `Handles (${fronts})`, amount: Math.round(handles) },
      { label: `Benchtop, ${baseMetres.toFixed(1)}m`, amount: Math.round(benchtop) },
      { label: 'Splashback', amount: Math.round(splashback) },
      ...(fill ? [{ label: `Fillers (${fillers})`, amount: Math.round(fill) }] : []),
      { label: 'Installation', amount: Math.round(install) },
    ],
  }
}
